import React from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { GoAlert, GoCheckCircle, GoInfo, GoXCircle } from 'react-icons/go';

const ICONS = { success: GoCheckCircle, warning: GoAlert, danger: GoXCircle, info: GoInfo };

// Cada recusa, com o mesmo nome do selo da lista do lote
function refusalOf(conflict) {
  if (conflict.reason === 'OUTSIDE_SERVICE_HOURS') return { variant: 'danger', label: 'Fora do horário' };
  if (conflict.reason === 'SCHEDULE_CONFLICT') return { variant: 'danger', label: 'Ocupado' };
  if (conflict.reason === 'ALREADY_SCHEDULED') return { variant: 'info', label: 'Já agendado' };
  if (conflict.reason === 'INVALID_TIME') return { variant: 'danger', label: 'Horário inválido' };
  return { variant: 'danger', label: 'Recusado' };
}

// Caixa com o título em cima e a mensagem embaixo: a mesma para livre, aviso e recusa
function Notice({ variant, title, children }) {
  const Icon = ICONS[variant];

  return (
    <Alert variant={variant} className='mb-0 py-2'>
      <Alert.Heading as='h6' className='d-flex align-items-center gap-2 mb-1 fw-semibold'>
        <Icon aria-hidden='true' /> {title}
      </Alert.Heading>
      {children}
    </Alert>
  );
}

// Horário 'HH:mm' de uma data do servidor, já no fuso do usuário ('yyyy-MM-ddTHH:mm:ss-03:00')
const time = (text) => text.substring(11, 16);
const byStart = (a, b) => a.data_agendamento_inicial.localeCompare(b.data_agendamento_inicial);

// Outros agendamentos do paciente no dia, um por linha. Sem a lista, fica a mensagem do servidor
function SameDayList({ conflict }) {
  const others = [...(conflict.existing_agendamentos ?? [])].sort(byStart);
  if (others.length === 0) return <div>{conflict.message}</div>;

  return (
    <>
      <div>{others.length === 1 ? 'O paciente já tem outro agendamento neste dia:' : `O paciente já tem outros ${others.length} agendamentos neste dia:`}</div>
      <ul className='mb-0 ps-3'>
        {others.map((agendamento) => (
          <li key={agendamento.id}>
            {time(agendamento.data_agendamento_inicial)}–{time(agendamento.data_agendamento_final)} com {agendamento.tecnico}{agendamento.status ? ` · ${agendamento.status}` : ''}
          </li>
        ))}
      </ul>
    </>
  );
}

// Quem ocupa o horário: com mais de um, um por linha
function OccupantsList({ conflict }) {
  const occupants = [...(conflict.existing_agendamentos ?? [])].sort(byStart);
  if (occupants.length < 2) return <div>{conflict.message}</div>;

  return (
    <ul className='mb-0 ps-3'>
      {occupants.map((agendamento) => (
        <li key={agendamento.id}>
          {agendamento.paciente} · {time(agendamento.data_agendamento_inicial)}–{time(agendamento.data_agendamento_final)}{agendamento.status ? ` · ${agendamento.status}` : ''}
        </li>
      ))}
    </ul>
  );
}

// Situação do agendamento sem repetição (novo ou aberto), logo abaixo da data e do horário.
// O título é sempre escrito, nunca só a cor. Os textos do servidor vão como texto, nunca como HTML
function content(check) {
  if (check.state === 'checking')
    return <small className='d-flex align-items-center gap-1 text-muted'><Spinner animation='border' size='sm' /> Verificando a agenda do doutor…</small>;

  if (check.state !== 'ready')
    return check.message ? <small className={check.state === 'failed' ? 'text-danger' : 'text-muted'}>{check.message}</small> : null;

  const { conflict } = check;

  // Sem recusa, o doutor atende e o horário está livre; o aviso do paciente vem embaixo, sem impedir
  if (!conflict?.blocking)
    return (
      <div className='d-grid gap-2'>
        <Notice variant='success' title='Livre'>
          <div>O doutor atende neste horário.</div>
        </Notice>
        {conflict?.reason === 'PACIENTE_SAME_DAY' ? (
          <Notice variant='warning' title='Aviso'>
            <SameDayList conflict={conflict} />
          </Notice>
        ) : ''}
      </div>
    );

  // Recusa: impede salvar. No agendamento aberto, a que já estava assim (sem mudar doutor, data ou horário) não impede e fica em amarelo
  const refusal = refusalOf(conflict);

  return (
    <Notice variant={check.blocking ? refusal.variant : 'warning'} title={refusal.label}>
      {conflict.reason === 'SCHEDULE_CONFLICT' ? <OccupantsList conflict={conflict} /> : <div>{conflict.message}</div>}
    </Notice>
  );
}

const AgendamentoCheck = ({ check }) => {
  const situation = content(check);

  return <div aria-live='polite' className={situation ? 'mb-2' : undefined}>{situation}</div>;
};

export default AgendamentoCheck;
